import { createSlice } from '@reduxjs/toolkit';
import {
  getAggregateMetrics,
  getAggregateCampaignsMetrics,
  getCampaignBreakdownMetrics,
  getUserBreakdownMetrics,
  getAnnualReportAggregatedMetrics,
  getSequenceCandidate,
  getUserBasicStatsAggregatedMetrics,
  uniqMessageReceive,
  getScheduledMessagesSearch,
  userMessageData,
  startReportGeneration,
  getReportingSequence,
  getSequenceMetrics,
} from '../actions/reporting';

const initialState = {
  uniqueCandidatesContacted: 0,
  unreadConversations: {
    data: [],
    loading: false,
    error: {},
    count: 0,
  },
  scheduledMessagesSearch: {
    data: [],
    loading: false,
    error: {},
    count: 0,
  },
  uniqMessageReceive: {
    loading: false,
    data: [],
    error: {},
  },
  userMessageData: {
    loading: false,
    data: [],
    error: {},
  },
  uniqueDoNotText: 0,
  aggregateMetrics: {},
  userBasicStatsMetrics: [],
  campaignsMetrics: {},
  userBreakdown: {
    loading: false,
    users: [],
    paging: {
      page: 1,
      items: 0,
    },
  },
  campaignBreakdown: {
    loading: false,
    campaigns: [],
    paging: {
      page: 1,
      items: 0,
    },
  },
  isMetricsLoading: false,
  reportLoadings: false,
  sequences: [],
  sequenceMetrics: {},
  sequenceCandidate: {},
};

const reportingSlice = createSlice({
  name: 'reporting',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(getAggregateMetrics.pending, (state) => {
      state.isMetricsLoading = true;
      state.error = false;
    });

    builder.addCase(getAggregateMetrics.fulfilled, (state, action) => {
      const [aggregateMetrics] = [...action.payload.metrics];
      state.aggregateMetrics = aggregateMetrics;
      state.isMetricsLoading = false;
    });

    builder.addCase(getAggregateMetrics.rejected, (state, action) => {
      state.isMetricsLoading = false;
      state.error = action.payload.error;
    });

    builder.addCase(getAnnualReportAggregatedMetrics.pending, (state) => {
      state.isMetricsLoading = true;
      state.error = false;
    });

    builder.addCase(getAnnualReportAggregatedMetrics.fulfilled, (state, action) => {
      const aggregateAnnualReportMetrics = action.payload.metrics;
      state.aggregateAnnualReportMetrics = aggregateAnnualReportMetrics;
      state.isMetricsLoading = false;
    });

    builder.addCase(getAnnualReportAggregatedMetrics.rejected, (state, action) => {
      state.isMetricsLoading = false;
      state.error = action.payload.error;
    });

    builder.addCase(getSequenceCandidate.pending, (state) => {
      state.isMetricsLoading = true;
    });

    builder.addCase(getSequenceCandidate.fulfilled, (state, action) => {
      state.isMetricsLoading = false;
      state.sequenceCandidate = action.payload.metrics;
    });

    builder.addCase(getSequenceCandidate.rejected, (state, action) => {
      state.isMetricsLoading = false;
      state.error = action.payload.error;
    });

    builder.addCase(getSequenceMetrics.pending, (state) => {
      state.isMetricsLoading = true;
    });

    builder.addCase(getSequenceMetrics.fulfilled, (state, action) => {
      state.sequenceMetrics = action.payload.metrics;
      state.isMetricsLoading = false;
    });

    builder.addCase(getSequenceMetrics.rejected, (state, action) => {
      state.isMetricsLoading = false;
      state.error = action.payload.error;
    });

    builder.addCase(getUserBasicStatsAggregatedMetrics.pending, (state) => {
      state.isMetricsLoading = true;
      state.error = false;
    });

    builder.addCase(getUserBasicStatsAggregatedMetrics.fulfilled, (state, action) => {
      const aggregateUserBasicStatsReportMetrics = action.payload.metrics;
      state.aggregateUserBasicStatsReportMetrics = aggregateUserBasicStatsReportMetrics;
      state.isMetricsLoading = false;
    });

    builder.addCase(getUserBasicStatsAggregatedMetrics.rejected, (state, action) => {
      state.isMetricsLoading = false;
      state.error = action.payload.error;
    });

    builder.addCase(getAggregateCampaignsMetrics.pending, (state) => {
      state.isMetricsLoading = true;
      state.error = false;
    });

    builder.addCase(getAggregateCampaignsMetrics.fulfilled, (state, action) => {
      const [campaignsMetrics] = [...action.payload.metrics];
      state.campaignsMetrics = campaignsMetrics;
      state.isMetricsLoading = false;
    });

    builder.addCase(getAggregateCampaignsMetrics.rejected, (state, action) => {
      state.isMetricsLoading = false;
      state.error = action.payload.error;
    });

    builder.addCase(getCampaignBreakdownMetrics.pending, (state) => {
      state.campaignBreakdown.loading = true;
      state.error = false;
    });

    builder.addCase(getCampaignBreakdownMetrics.fulfilled, (state, action) => {
      const { paging = {}, campaigns } = action.payload;
      state.campaignBreakdown.loading = false;
      state.campaignBreakdown.campaigns = campaigns;
      state.campaignBreakdown.paging = paging;
    });

    builder.addCase(getCampaignBreakdownMetrics.rejected, (state, action) => {
      state.campaignBreakdown.loading = false;
      state.error = action.payload.error;
    });

    builder.addCase(getUserBreakdownMetrics.pending, (state) => {
      state.userBreakdown.loading = true;
      state.isLoading = true;
      state.error = false;
    });

    builder.addCase(getUserBreakdownMetrics.fulfilled, (state, action) => {
      const { paging = {}, users } = action.payload;
      state.userBreakdown.loading = false;
      state.userBreakdown.users = users;
      state.userBreakdown.paging = paging;
      state.isLoading = false;
    });

    builder.addCase(getUserBreakdownMetrics.rejected, (state, action) => {
      state.userBreakdown.loading = false;
      state.error = action.payload.error;
      state.isLoading = false;
    });

    builder.addCase(uniqMessageReceive.pending, (state) => {
      state.uniqMessageReceive.loading = true;
    });

    builder.addCase(uniqMessageReceive.rejected, (state, action) => {
      state.uniqMessageReceive.loading = false;
      state.uniqMessageReceive.error = action.payload.error;
    });

    builder.addCase(uniqMessageReceive.fulfilled, (state, action) => {
      state.uniqMessageReceive.loading = false;
      state.uniqMessageReceive.data = action.payload.metrics;
      state.uniqMessageReceive.error = false;
    });

    builder.addCase(userMessageData.pending, (state) => {
      state.userMessageData.loading = true;
    });

    builder.addCase(userMessageData.rejected, (state, action) => {
      state.userMessageData.loading = false;
      state.userMessageData.error = action.payload.error;
    });

    builder.addCase(userMessageData.fulfilled, (state, action) => {
      state.userMessageData.loading = false;
      state.userMessageData.data = action.payload.metrics;
      state.userMessageData.error = {};
    });

    builder.addCase(getScheduledMessagesSearch.pending, (state) => {
      state.scheduledMessagesSearch.loading = true;
    });

    builder.addCase(getScheduledMessagesSearch.fulfilled, (state, action) => {
      state.scheduledMessagesSearch.loading = false;
      state.scheduledMessagesSearch.count = action.payload.paging.items || 0;
      state.scheduledMessagesSearch.data = action.payload.scheduledMessages;
      state.isCancelMessage = false;
    });

    builder.addCase(getScheduledMessagesSearch.rejected, (state, action) => {
      state.scheduledMessagesSearch.loading = false;
      state.scheduledMessagesSearch.error = action.payload.error;
    });

    builder.addCase(startReportGeneration.pending, (state) => {
      state.reportLoadings = true;
    });

    builder.addCase(startReportGeneration.fulfilled, (state) => {
      state.reportLoadings = false;
    });

    builder.addCase(startReportGeneration.rejected, (state) => {
      state.reportLoadings = false;
    });

    builder.addCase(getReportingSequence.fulfilled, (state, action) => {
      state.sequences = action.payload.sequences;
      state.isSequencesLoading = false;
    });

    builder.addCase(getReportingSequence.pending, (state) => {
      state.isSequencesLoading = true;
    });
  },
});

export default reportingSlice;
